import { useEffect, useState } from "react";
import { Form, Formik } from "formik";

import { ArrowBackIos } from "@mui/icons-material";
import { Grid, Box, IconButton, InputAdornment, Typography } from "@mui/material";

import { StyledGridContainer } from "../../../components/StyledLayout";
import { NoVisibleIcon, VisibleIcon } from "../../../components/Icons";
import { StyledTextField } from "../../../components/StyledTextField";
import { StyledButton } from "../../../components/StyledButton";
import { StyledCard } from "../../../components/StyledCard";

import { routesConfig } from "../../../config/routes";

import { useContextualNavigate } from "../../../hooks/ContextualNavigations";

import { initialValuesFormikUpdatePassword } from "../form/initialValues";
import { ChangePasswordFormFields } from "../form/interfaceValues";
import { PasswordStrengthBar } from "../../../components/PasswordStrengthBar";
import { validationSchemaChangePassword } from "../form/validationSchema";
import { useChangePasswordSubmit } from "./hook/useChangePasswordSubmit";

const ChangePasswordDefault: React.FC = () => {
  let navigate = useContextualNavigate();

  const handleClickGoToBackHome = () => {
    navigate(`${routesConfig.LOGIN}`);
  };

  const { handleSubmit, successChangePassword } = useChangePasswordSubmit();

  const [values, setValues] = useState({
    showPassword: false,
    showConfirmPassword: false,
    hasFocus: false,
  });

  const handleClickShowPassword = () => {
    setValues({ ...values, showPassword: !values.showPassword });
  };

  const handleClickShowConfirmPassword = () => {
    setValues({ ...values, showConfirmPassword: !values.showConfirmPassword });
  };

  const handleOnChangeHasFocus = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const input = (event.target as HTMLInputElement).value;

    if (input.length > 0) {
      setValues({
        ...values,
        hasFocus: true,
      });
    } else {
      setValues({
        ...values,
        hasFocus: false,
      });
    }
  };

  useEffect(() => {}, [successChangePassword]);

  const handleMouseDownPassword = (event: any) => {
    event.preventDefault();
  };

  return (
    <Formik
      validateOnMount={true}
      initialValues={initialValuesFormikUpdatePassword}
      validationSchema={validationSchemaChangePassword}
      onSubmit={handleSubmit}
    >
      {(propsFormik) => {
        const { values: formValues, isValid, isSubmitting, touched, errors, handleChange, handleBlur } = propsFormik;
        return(
          <Form>
            <StyledGridContainer>
              <StyledCard>
                <IconButton
                  onClick={handleClickGoToBackHome}
                  edge="end"
                  sx={{ display: "flex" }}
                >
                  <ArrowBackIos sx={{ color: "primary.main", opacity: "1" }} />
                </IconButton>
  
                {successChangePassword ? (
                  <Box>
                    <Box>
                      <img
                        src="/images/atem-logotipo.png"
                        alt="logotipo"
                        style={{ marginBottom: "2rem" }}
                      />
  
                      <Typography
                        color="primary"
                        sx={{
                          fontSize: "2.5rem",
                          fontWeight: "bold",
                          fontFamily: "Poppins",
                        }}
                      >
                        Senha Cadastrada
                      </Typography>
  
                      <Typography
                        color="subTitles.main"
                        sx={{
                          fontWeight: 500,
                          fontSize: "1.6rem",
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        Sua nova senha foi cadastrada com sucesso!
                      </Typography>
                    </Box>
  
                    <StyledButton
                      variant="contained"
                      color="primary"
                      onClick={handleClickGoToBackHome}
                      loading={isSubmitting}
                    >
                      Fazer Login
                    </StyledButton>
                  </Box>
                ) : (
                  <Box>
                    <Box sx={{ my: 4 }}>
                      <Typography
                        color="primary"
                        sx={{
                          fontSize: "3rem",
                          fontWeight: "bold",
                          fontFamily: "Poppins",
                        }}
                      >
                        Esqueceu a senha?
                      </Typography>
                    </Box>
  
                    <Box sx={{ my: 6, mx: { xs: 0, md: 4 } }}>
                      <Grid container sx={{ gap:{xs:1,lg:2} }}>
                        <Grid item xs={12}>
                          <StyledTextField
                            name={ChangePasswordFormFields.newPass}
                            value={formValues.newPass}
                            type={values.showPassword ? "text" : "password"}
                            onChange={(e) => {
                              handleChange(e);
                              handleOnChangeHasFocus(e);
                            }}
                            onBlur={handleBlur}
                            labelprops={{
                              label: "Nova senha",
                              hasTouched: touched.newPass,
                              hasErros: errors.newPass,
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    edge="end"
                                    onClick={handleClickShowPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    sx={{ m: 0 }}
                                  >
                                    {values.showPassword ? (
                                      <NoVisibleIcon
                                        sx={{
                                          color: "black",
                                          opacity: values.hasFocus ? "1" : "0.2",
                                        }}
                                      />
                                    ) : (
                                      <VisibleIcon
                                        sx={{
                                          color: "primary.main",
                                          opacity: values.hasFocus ? "1" : "0.2",
                                        }}
                                      />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <StyledTextField
                            name={ChangePasswordFormFields.confirmPass}
                            value={formValues.confirmPass}
                            type={values.showConfirmPassword ? "text" : "password"}
                            onChange={(e) => {
                              handleChange(e);
                              handleOnChangeHasFocus(e);
                            }}
                            onBlur={handleBlur}
                            labelprops={{
                              label: "Confirme a nova senha",
                              hasTouched: touched.confirmPass,
                              hasErros: errors.confirmPass,
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    edge="end"
                                    onClick={handleClickShowConfirmPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    sx={{ m: 0 }}
                                  >
                                    {values.showConfirmPassword ? (
                                      <NoVisibleIcon
                                        sx={{
                                          color: "black",
                                          opacity: values.hasFocus ? "1" : "0.2",
                                        }}
                                      />
                                    ) : (
                                      <VisibleIcon
                                        sx={{
                                          color: "primary.main",
                                          opacity: values.hasFocus ? "1" : "0.2",
                                        }}
                                      />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <PasswordStrengthBar
                            newPass={formValues.newPass}
                            newPassConfirmation={formValues.confirmPass}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                    
                    <StyledButton
                      variant="contained"
                      color="primary"
                      type="submit"
                      loading={isSubmitting}
                      disabled={!isValid}
                    >
                      Alterar Senha
                    </StyledButton>
                  </Box>
                )}
              </StyledCard>
            </StyledGridContainer>
          </Form>
        )
      }}
    </Formik>
  );
};

export default ChangePasswordDefault;
